import StakeholderCombobox from "@app/components/stakeholder-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import { useState } from "react";
import { commitMutation, graphql } from "react-relay";

import type {
  createRuleDialog_CreateRuleMutation,
  TxnClassificationEnum,
} from "@app/queries/createRuleDialog_CreateRuleMutation.graphql";

const createRuleMutation = graphql`
  mutation createRuleDialog_CreateRuleMutation(
    $input: CreateRuleInputObject!
  ) {
    createRule(input: $input) {
      rule {
        id
        matchesCount
      }
      errors {
        message
      }
    }
  }
`;

const CLASSIFICATIONS: { value: TxnClassificationEnum; label: string }[] = [
  { value: "FRAUDULENT", label: "Fraudulent" },
  { value: "LOAN_PROCEEDS", label: "Loan proceeds" },
  { value: "LOAN_REPAYMENT", label: "Loan repayment" },
  { value: "DIVIDEND", label: "Dividend" },
  { value: "SALARY", label: "Salary / wages" },
  { value: "TAX_PAYMENT", label: "Tax payment" },
  { value: "TAX_REFUND", label: "Tax payback" },
  { value: "REFUND", label: "Refund" },
  { value: "CHARGEBACK", label: "Chargeback" },
  { value: "PROCESSOR_FEE", label: "Processor fee" },
];

// Build a regex from a description: escape metachars, then optionally loosen by
// stripping a trailing run of digits/whitespace (so "ACME PMT 00481" → a prefix
// that catches the next import's "ACME PMT 00733").
function regexFromDescription(description: string, loosen: boolean): string {
  let base = description.trim();
  if (loosen) base = base.replace(/[\s\d]+$/, "").trim();
  return base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

type TargetType = "ATTRIBUTE" | "INTERNAL" | "CLASSIFY" | "NATURE";
type NatureOption = { id: string; name: string };

// Create a standing rule (or apply a one-off classification / internal link)
// straight from a transaction row in the transactions context menu.
export default function CreateRuleDialog({
  description,
  stakeholders,
  accounts,
  natures,
  onClose,
  onApplied,
}: {
  description: string;
  stakeholders: ReadonlyArray<StakeholderOption>;
  accounts: ReadonlyArray<AccountOption>;
  natures: ReadonlyArray<NatureOption>;
  onClose: () => void;
  onApplied: (message: string) => void;
}) {
  const [target, setTarget] = useState<TargetType>("ATTRIBUTE");
  const [loosen, setLoosen] = useState(true);
  const [regex, setRegex] = useState(() =>
    regexFromDescription(description, true)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setLoosenAndRegex = (next: boolean) => {
    setLoosen(next);
    setRegex(regexFromDescription(description, next));
  };

  const switchTarget = (next: TargetType) => {
    // Retain the regex; clear any error.
    setTarget(next);
    setError(null);
  };

  const fail = (message: string) => {
    setSaving(false);
    setError(message);
  };

  // Create a standing rule of any flavor, then report how many it matched.
  const createRule = (
    targetFields: {
      stakeholderId?: string;
      stakeholderName?: string;
      targetType?: "STAKEHOLDER" | "CLASSIFICATION" | "INTERNAL_LINK" | "NATURE";
      targetClassification?: TxnClassificationEnum;
      destinationBankAccountId?: string;
      destinationAccountName?: string;
      expenseNatureId?: string;
    }
  ) => {
    setSaving(true);
    setError(null);
    commitMutation<createRuleDialog_CreateRuleMutation>(environment, {
      mutation: createRuleMutation,
      variables: {
        input: {
          ...targetFields,
          regex,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: (res) => {
        setSaving(false);
        const payload = res.createRule;
        if (payload?.errors.length) {
          setError(payload.errors[0].message);
          return;
        }
        if (!payload?.rule) {
          setError("Could not create the rule.");
          return;
        }
        onApplied(
          `Rule created — matches ${payload.rule.matchesCount} transaction${
            payload.rule.matchesCount === 1 ? "" : "s"
          }.`
        );
      },
      onError: (err) => fail(err.message),
    });
  };

  // Attribute: create a standing attribution rule (existing or new counterparty).
  const createAttributeRule = (choice: {
    stakeholderId?: string;
    name?: string;
  }) =>
    createRule({
      targetType: "STAKEHOLDER",
      stakeholderId: choice.stakeholderId,
      stakeholderName: choice.name,
    });

  // Internal: create a standing internal-link rule that sets the destination account
  // on all matching transactions (existing destination or one created on the fly).
  const createInternalLinkRule = (choice: {
    bankAccountId?: string;
    name?: string;
  }) =>
    createRule({
      targetType: "INTERNAL_LINK",
      destinationBankAccountId: choice.bankAccountId,
      destinationAccountName: choice.name,
    });

  // Classify: create a standing classification rule for matching transactions.
  const createClassificationRule = (classification: TxnClassificationEnum) =>
    createRule({ targetType: "CLASSIFICATION", targetClassification: classification });

  // Nature: create a standing rule that stamps the expense nature (by-nature axis).
  const createNatureRule = (expenseNatureId: string) =>
    createRule({ targetType: "NATURE", expenseNatureId });

  // All three flavors are standing rules now, so all need a match pattern.
  const needsRegex = true;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[520px] p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800">
          Create rule from transaction
        </h2>

        <div className="flex gap-1 text-xs">
          {(["ATTRIBUTE", "INTERNAL", "CLASSIFY", "NATURE"] as TargetType[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => switchTarget(t)}
              className={`px-3 py-1.5 rounded border ${
                target === t
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t === "ATTRIBUTE"
                ? "Attribute"
                : t === "INTERNAL"
                ? "Internal"
                : t === "CLASSIFY"
                ? "Classify"
                : "Nature"}
            </button>
          ))}
        </div>

        {needsRegex && (
          <div className="space-y-1">
            <label className="text-xs text-gray-500">Match regex</label>
            <input
              value={regex}
              onChange={(e) => setRegex(e.target.value)}
              className="w-full rounded border px-2 py-1.5 font-mono text-xs"
            />
            <label className="flex items-center gap-1.5 text-xs text-gray-500">
              <input
                type="checkbox"
                checked={loosen}
                onChange={(e) => setLoosenAndRegex(e.target.checked)}
              />
              Loosen trailing digits/IDs
            </label>
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        {target === "ATTRIBUTE" && (
          <div>
            <p className="text-xs text-gray-500 mb-1">
              Attribute matching transactions to:
            </p>
            <StakeholderCombobox
              options={stakeholders}
              autoFocus={false}
              onPick={(choice) => !saving && createAttributeRule(choice)}
            />
          </div>
        )}

        {target === "INTERNAL" && (
          <div>
            <p className="text-xs text-gray-500 mb-1">
              Link matching transactions to a destination account (existing or new):
            </p>
            <AccountCombobox
              options={accounts}
              autoFocus={false}
              onPick={(choice) => !saving && createInternalLinkRule(choice)}
            />
          </div>
        )}

        {target === "CLASSIFY" && (
          <div className="flex flex-wrap gap-1.5">
            {CLASSIFICATIONS.map((c) => (
              <button
                key={c.value}
                type="button"
                disabled={saving}
                onClick={() => createClassificationRule(c.value)}
                className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {target === "NATURE" && (
          <div>
            <p className="text-xs text-gray-500 mb-1">
              Tag matching transactions with an expense nature:
            </p>
            {natures.length === 0 ? (
              <p className="text-xs text-gray-400">
                No natures yet — add some on the Expense nature page first.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {natures.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    disabled={saving}
                    onClick={() => createNatureRule(n.id)}
                    className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                  >
                    {n.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
